import React from 'react';
import Card from './ui/Card';

export interface SystemControlsBarProps {
  darkMode: boolean;
  onSetDarkMode: (isDark: boolean) => void;
  toggleMode: 'static' | 'auto' | 'live';
  onSetToggleMode: (mode: 'static' | 'auto' | 'live') => void;
  systemOnline: boolean;
  lastUpdateText: string;
  isRefreshing: boolean;
  showAnimatedBackground: boolean;
  onSetShowAnimatedBackground: (show: boolean) => void;
  onAdminAccess?: () => void;
}

const SystemControlsBar: React.FC<SystemControlsBarProps> = ({
  darkMode,
  onSetDarkMode,
  toggleMode,
  onSetToggleMode,
  systemOnline,
  lastUpdateText,
  isRefreshing,
  showAnimatedBackground,
  onSetShowAnimatedBackground,
  onAdminAccess,
}) => {
  const toggleGroupBase =
    'flex items-center backdrop-blur-sm rounded-lg p-1 border border-gray-300 dark:border-black';
  const toggleLabelBase =
    'flex items-center space-x-1.5 cursor-pointer px-3 py-2 rounded-md text-xs sm:text-sm transition-all duration-200 font-medium';
  const toggleLabelActive =
    'bg-white dark:bg-slate-700/50 backdrop-blur-sm border border-gray-300 dark:border-black hover:bg-gray-50 dark:hover:bg-slate-600/50 transition-all duration-200 text-text-primary-light dark:text-white';
  const toggleLabelInactive =
    'text-text-primary-light dark:text-text-primary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark';

  const toggleGroupStyle = {
    backgroundColor: '#f3f4f6',
  };

  const toggleGroupStyleDark = {
    backgroundColor: '#000000',
  };

  const MoonIcon = () => (
    <svg
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z' />
    </svg>
  );
  const SunIcon = () => (
    <svg
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <circle cx='12' cy='12' r='5' />
      <path d='M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42' />
    </svg>
  );
  const SparklesIcon = () => (
    <svg
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.582a.5.5 0 0 1 0 .963L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z' />
      <path d='M20 3v4' />
      <path d='M22 5h-4' />
      <path d='M4 17v2' />
      <path d='M5 18H3' />
    </svg>
  );
  const ShieldIcon = () => (
    <svg
      width='16'
      height='16'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <path d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z' />
    </svg>
  );
  const BoltIcon = () => (
    <svg
      viewBox='0 0 24 24'
      width='18'
      height='18'
      fill='currentColor'
      className={`mr-1 ${toggleMode === 'live' ? 'text-yellow-400 animate-pulse' : ''}`}
    >
      <path d='M13 3L4 14h7v7l9-11h-7V3z' />
    </svg>
  );

  return (
    <Card className='mb-6 p-4 sm:p-6 shadow-md'>
      <div className='flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 text-xs sm:text-sm'>
        <div className='flex items-center gap-2'>
          <button
            onClick={() => onSetDarkMode(!darkMode)}
            className='flex items-center justify-center w-10 h-10 bg-slate-200/50 dark:bg-slate-700/50 backdrop-blur-sm rounded-lg border border-gray-300 dark:border-black hover:bg-slate-300/50 dark:hover:bg-slate-600/50 transition-all duration-200 text-slate-700 dark:text-slate-300'
            title={darkMode ? 'Switch to light mode' : 'Switch to dark mode'}
          >
            {darkMode ? <SunIcon /> : <MoonIcon />}
          </button>

          <button
            onClick={() => onSetShowAnimatedBackground(!showAnimatedBackground)}
            className={`flex items-center justify-center w-10 h-10 bg-slate-200/50 dark:bg-slate-700/50 backdrop-blur-sm rounded-lg border border-gray-300 dark:border-black hover:bg-slate-300/50 dark:hover:bg-slate-600/50 transition-all duration-200 ${
              showAnimatedBackground
                ? 'text-slate-700 dark:text-slate-300'
                : 'text-slate-400 dark:text-slate-500'
            }`}
            title={
              showAnimatedBackground
                ? 'Turn off animated background'
                : 'Turn on animated background'
            }
          >
            <SparklesIcon />
          </button>

          {onAdminAccess && (
            <button
              onClick={onAdminAccess}
              className='flex items-center justify-center w-10 h-10 bg-blue-200/50 dark:bg-blue-700/50 backdrop-blur-sm rounded-lg border border-blue-300 dark:border-blue-600 hover:bg-blue-300/50 dark:hover:bg-blue-600/50 transition-all duration-200 text-blue-700 dark:text-blue-300'
              title='Access Admin Console'
            >
              <ShieldIcon />
            </button>
          )}
        </div>

        <div
          className={`${toggleGroupBase} flex-wrap justify-center`}
          style={darkMode ? toggleGroupStyleDark : toggleGroupStyle}
        >
          {(['static', 'auto', 'live'] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => onSetToggleMode(mode)}
              className={`${toggleLabelBase} ${toggleMode === mode ? toggleLabelActive : toggleLabelInactive}`}
            >
              {mode === 'live' && <BoltIcon />}
              <span className='capitalize'>{mode}</span>
            </button>
          ))}
        </div>

        <div className='flex flex-col items-center sm:ml-auto gap-1 text-center'>
          {' '}
          {/* sm:ml-auto to push to right */}
          <div className='flex items-center'>
            <span
              className={`flex-shrink-0 w-2.5 h-2.5 rounded-full mr-1.5 ${
                systemOnline ? 'bg-emerald-500' : 'bg-red-500'
              } ${isRefreshing && systemOnline ? 'animate-pulse' : ''}`}
              title={systemOnline ? 'Online' : 'Offline'}
            ></span>
            <span className='text-text-secondary-light dark:text-text-secondary-dark text-xs'>
              {systemOnline ? 'Online' : 'Offline'}
            </span>
          </div>
          {lastUpdateText && (
            <span
              className='text-text-secondary-light dark:text-text-secondary-dark truncate text-xs'
              title={lastUpdateText}
            >
              {isRefreshing && toggleMode !== 'static'
                ? 'Refreshing...'
                : lastUpdateText}
            </span>
          )}
        </div>
      </div>
    </Card>
  );
};

export default SystemControlsBar;
